import type { Building } from "building-types/building/buildingTypes";
import type { GeneratedDimension, GeneratedDimensionMap } from "building-types/dimensions/dimensionTypes";
import { InteriorDimensionType } from "building-types/settings/settingsTypesEnum";
import type { RWTable, Table } from "common/data/table";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import { makeExteriorFloorplanDimensionsByLevel } from "../utils/exteriorDimensions";
import {
  foundationPropPredicate,
  getFoundationProps,
  makeFoundationConditionTags,
  makeFoundationPerimeters,
  makeFoundationPlanDimensionsByLevel,
} from "../utils/foundation";
import { makeInteriorFloorPlanDimensionsByLevel } from "../utils/interiorDimensions";
import { memoAsync } from "../utils/memo";
import { makePropLocations } from "../utils/propDimensions";

function select({
  levels,
  planarGraphs,
  mirror,
  rooms,
  roomLevelIndices,
  roomsByLevel,
  settings,
  walls,
  floorPlates,
  dimensionBehavior,
  porches,
  foundations,
  floorSystems,
  foundationSuperSystems,
  foundationPaths,
  foundationHorizontalAssembliesPaths,
  floorPaths,
  electricalSystemView,
  wallMasses,
  props,
  enabledFeatures,
  doc: { levels: levelDefs, compositeLevels, assets, foundationAssemblies },
  componentToTransforms,
}: Pick<
  Building,
  | "levels"
  | "planarGraphs"
  | "mirror"
  | "rooms"
  | "roomLevelIndices"
  | "roomsByLevel"
  | "settings"
  | "walls"
  | "floorPlates"
  | "dimensionBehavior"
  | "porches"
  | "foundations"
  | "floorSystems"
  | "foundationSuperSystems"
  | "foundationPaths"
  | "foundationHorizontalAssembliesPaths"
  | "floorPaths"
  | "electricalSystemView"
  | "wallMasses"
  | "props"
  | "enabledFeatures"
  | "doc"
  | "componentToTransforms"
>) {
  return {
    levels,
    planarGraphs,
    mirror,
    rooms,
    roomLevelIndices,
    roomsByLevel,
    settings,
    walls,
    floorPlates,
    dimensionBehavior,
    porches,
    foundations,
    floorSystems,
    foundationSuperSystems,
    foundationPaths,
    foundationHorizontalAssembliesPaths,
    floorPaths,
    electricalSystemView,
    wallMasses,
    props,
    enabledFeatures,
    levelDefs,
    compositeLevels,
    assets,
    foundationAssemblies,
    componentToTransforms,
  };
}

async function generate(opts: ReturnType<typeof select>): Promise<{
  exteriorDimensions: Table<GeneratedDimensionMap>;
  foundationExteriorDimensions: Table<GeneratedDimension[]>;
  foundationInteriorDimensions: Table<GeneratedDimension[]>;
  interiorDimensions: Table<GeneratedDimensionMap>;
  dimensions: Table<GeneratedDimension>;
  dimensionsByEntityHandleHash: Table<GeneratedDimension>;
}> {
  const exteriorDimensions: RWTable<GeneratedDimensionMap> = {};
  const foundationExteriorDimensions: RWTable<GeneratedDimension[]> = {};
  const foundationInteriorDimensions: RWTable<GeneratedDimension[]> = {};
  const interiorDimensions: RWTable<GeneratedDimensionMap> = {};

  const building = {
    ...opts,
    doc: { levels: opts.levelDefs, compositeLevels: opts.compositeLevels, assets: opts.assets },
  };

  for (const levelId in opts?.levels ?? {}) {
    exteriorDimensions[levelId] = makeExteriorFloorplanDimensionsByLevel({ building, levelId });

    const { exteriorPerimeters } = makeFoundationPerimeters(building, levelId);
    const { interiorDimensions: foundationInteriors, exteriorDimensions: foundationExteriors } =
      await makeFoundationPlanDimensionsByLevel(
        building,
        levelId,
        makeFoundationConditionTags(building, building.foundationAssemblies, levelId),
        await makePropLocations(
          building,
          getFoundationProps(building, foundationPropPredicate(building, levelId)),
          exteriorPerimeters,
        ),
      );

    interiorDimensions[levelId] = makeInteriorFloorPlanDimensionsByLevel({ building, levelId });

    foundationExteriorDimensions[levelId] = foundationExteriors;
    foundationInteriorDimensions[levelId] = foundationInteriors;
  }

  for (const compositeLevelId in opts.compositeLevels ?? {}) {
    exteriorDimensions[compositeLevelId] = makeExteriorFloorplanDimensionsByLevel({
      building,
      levelId: compositeLevelId,
    });
    if (building.settings.planset.interiorDimensionType === InteriorDimensionType.BY_ROOM_GROUP) {
      interiorDimensions[compositeLevelId] = makeInteriorFloorPlanDimensionsByLevel({
        building,
        levelId: compositeLevelId,
      });
    }
  }

  const dimensions: RWTable<GeneratedDimension> = {};
  const dimensionsByEntityHandleHash: RWTable<GeneratedDimension> = {};

  [exteriorDimensions, interiorDimensions].forEach((table) => {
    for (const id in table) {
      const multimap = table[id];
      for (const groupId in multimap) {
        const group = multimap[groupId];
        for (const d of group) {
          dimensions[d.id] = d;
          if (d.docEntityHandle) {
            dimensionsByEntityHandleHash[docEntityHandleHash(d.docEntityHandle)] = d;
          }
        }
      }
    }
  });

  [foundationExteriorDimensions, foundationInteriorDimensions].forEach((table) => {
    for (const id in table) {
      const dimension = table[id];
      for (const d of dimension) {
        dimensions[d.id] = d;
        if (d.docEntityHandle) {
          dimensionsByEntityHandleHash[docEntityHandleHash(d.docEntityHandle)] = d;
        }
      }
    }
  });

  return {
    dimensions,
    dimensionsByEntityHandleHash,
    exteriorDimensions,
    foundationExteriorDimensions,
    foundationInteriorDimensions,
    interiorDimensions,
  };
}

export default memoAsync(select, generate);
